/**
 * wc-mobile-nav.js
 *
 * Converts the native <details>/<summary> accordion mobile menu into a
 * drill-down slide navigation:
 *
 *   Tap "Bags"              → slides to show level 2 (Shop By Category, …)
 *   Tap "Shop By Category"  → slides to show level 3 (Best Sellers, …)
 *   Tap "Back"              → slides back to the parent level
 *
 * Key insight: child panels (.details__content) are nested inside their parent
 * <ul>. We can't hide a parent and keep a child visible, so on drill-in we
 * EXTRACT the child panel from the <details> and place it as a direct child
 * of the nav container. On drill-back we put it back.
 */
(function () {
  'use strict';

  var DRAWER_ID   = 'slideout-mobile-navigation';
  var NAV_SEL     = '.mobile-menu__navigation';
  var BUILT_ATTR  = 'data-wc-drilldown';
  var ANIM_MS     = 280;

  /* ── helpers ──────────────────────────────────────────────────────────── */

  function el(tag, cls) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    return e;
  }

  /* ── Back button factory ─────────────────────────────────────────────── */

  function makeBackBtn(label, onclick) {
    var btn = el('button', 'wc-dnav-back');
    btn.setAttribute('type', 'button');
    btn.setAttribute('aria-label', 'Go back');
    btn.innerHTML =
      '<span class="wc-dnav-back__chevron">' +
        '<svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
      '</span>' +
      '<span class="wc-dnav-back__label"></span>';
    btn.querySelector('.wc-dnav-back__label').textContent = label;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      onclick();
    });
    return btn;
  }

  /* ── init ─────────────────────────────────────────────────────────────── */

  function init() {
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;

    var navBlock = drawer.querySelector(NAV_SEL);
    if (!navBlock || navBlock.getAttribute(BUILT_ATTR)) return;
    navBlock.setAttribute(BUILT_ATTR, '1');

    // The root <ul class="menu__list">
    var rootUl = navBlock.querySelector(':scope > ul.menu__list');
    if (!rootUl) return;

    // Mark the nav block as a drilldown container.
    navBlock.classList.add('wc-dnav');

    // history = stack of { detailsEl, contentEl } objects.
    // The contentEl has been extracted from detailsEl and placed as a direct
    // child of navBlock while it's the active panel.
    var history = [];

    /** The currently visible panel element (rootUl initially). */
    function activePanel() {
      return history.length === 0 ? rootUl : history[history.length - 1].contentEl;
    }

    /**
     * Drill INTO a <details> element.
     */
    function drillInto(detailsEl) {
      var contentEl = detailsEl.querySelector(':scope > .details__content');
      if (!contentEl) return;

      // Get the title from the summary link.
      var summaryLink = detailsEl.querySelector(':scope > summary .details__link, :scope > summary .menu__link');
      var title = summaryLink ? summaryLink.textContent.trim() : 'Back';

      // Inject a Back button if not already present.
      if (!contentEl.querySelector(':scope > .wc-dnav-back')) {
        var backBtn = makeBackBtn(title, function () { drillBack(); });
        contentEl.insertBefore(backBtn, contentEl.firstChild);
      }

      // Open the <details> natively so contentEl becomes visible in the DOM.
      detailsEl.setAttribute('open', '');

      var currentPanel = activePanel();

      // EXTRACT contentEl from the <details> and place it as a direct child
      // of navBlock. This is the key step — it breaks the nesting so we can
      // hide the parent without hiding the child.
      navBlock.appendChild(contentEl);

      // Animate: slide current panel left, new panel in from right.
      currentPanel.classList.add('wc-dnav--exiting-left');
      contentEl.classList.add('wc-dnav--entering-right');

      history.push({ detailsEl: detailsEl, contentEl: contentEl });

      setTimeout(function () {
        currentPanel.classList.remove('wc-dnav--exiting-left');
        currentPanel.classList.add('wc-dnav--hidden');

        contentEl.classList.remove('wc-dnav--entering-right');
      }, ANIM_MS);
    }

    /**
     * Drill BACK to the parent level.
     */
    function drillBack() {
      if (history.length === 0) return;

      var current = history.pop();
      var parentPanel = activePanel(); // after pop, this is the parent

      // Un-hide parent.
      parentPanel.classList.remove('wc-dnav--hidden');
      parentPanel.classList.add('wc-dnav--entering-left');

      // Slide current panel out to the right.
      current.contentEl.classList.add('wc-dnav--exiting-right');

      setTimeout(function () {
        parentPanel.classList.remove('wc-dnav--entering-left');
        current.contentEl.classList.remove('wc-dnav--exiting-right');

        // PUT contentEl BACK into its original <details>.
        current.detailsEl.appendChild(current.contentEl);
        current.detailsEl.removeAttribute('open');
      }, ANIM_MS);
    }

    /**
     * Reset all levels instantly (e.g. when drawer closes).
     */
    function resetAll() {
      while (history.length > 0) {
        var entry = history.pop();
        // Remove animation classes.
        entry.contentEl.classList.remove(
          'wc-dnav--entering-right', 'wc-dnav--exiting-right',
          'wc-dnav--entering-left', 'wc-dnav--exiting-left',
          'wc-dnav--hidden'
        );
        // Put contentEl back into its <details> and close it.
        entry.detailsEl.appendChild(entry.contentEl);
        entry.detailsEl.removeAttribute('open');
      }
      rootUl.classList.remove(
        'wc-dnav--hidden', 'wc-dnav--exiting-left', 'wc-dnav--entering-left'
      );
    }

    /* ── Intercept <details> toggle ─────────────────────────────────────── */

    navBlock.addEventListener('click', function (e) {
      var summary = e.target.closest('summary.details__summary');
      if (!summary) return;

      // Only intercept within our nav block.
      var details = summary.closest('details');
      if (!details || !navBlock.contains(details)) return;

      // Prevent the native toggle + link navigation.
      e.preventDefault();
      e.stopPropagation();

      // If already open, ignore (shouldn't happen in drill-down).
      if (details.hasAttribute('open')) return;

      drillInto(details);
    });

    /* ── Reset on drawer close ─────────────────────────────────────────── */

    drawer.addEventListener('toggle', function () {
      if (!drawer.matches(':popover-open')) {
        setTimeout(resetAll, 300);
      }
    });

    // Fallback for themes using class-based slideout instead of popover.
    document.addEventListener('click', function (e) {
      if (e.target.closest('.js-slideout-close')) {
        setTimeout(resetAll, 300);
      }
    });
  }

  /* ── Boot ─────────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      // Wait for wc-mega-menu.js to finish relocating panels first.
      requestAnimationFrame(function () {
        requestAnimationFrame(init);
      });
    });
  } else {
    requestAnimationFrame(function () {
      requestAnimationFrame(init);
    });
  }
})();
